import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';

// ============================================================================
// Types
// ============================================================================

export type RequirementStatus =
  | 'pending'
  | 'sent'
  | 'received'
  | 'approved'
  | 'waived'
  | 'overdue';

export interface DocumentRequirementType {
  id: string;
  code: string;
  display_name: string | null;
  description: string | null;
  template_document_id: string | null;
  default_recipient_role: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DocumentRequirementRule {
  id: string;
  requirement_type_id: string;
  project_type_id: string;
  trigger_workflow_stage: string;
  due_offset_days: number;
  recipient_role: string;
  condition_json: Record<string, string[]> | null;
  priority: number;
  active: boolean;
  created_at: string;
  updated_at: string;
  requirement_type_code?: string;
  requirement_type_name?: string;
  project_type_name?: string;
}

export interface ProjectDocumentRequirement {
  id: string;
  project_id: string;
  requirement_type_id: string;
  rule_id: string | null;
  status: RequirementStatus;
  due_date: string | null;
  recipient_name: string | null;
  recipient_email: string | null;
  custom_recipient_email: string | null;
  assigned_to_user_id: string | null;
  fulfillment_document_id: string | null;
  waived_reason: string | null;
  notes: string | null;
  sent_at: string | null;
  received_at: string | null;
  created_at: string;
  updated_at: string;
  requirement_type_name?: string;
  requirement_type_code?: string;
}

// ============================================================================
// Query keys
// ============================================================================

export const TYPES_QUERY_KEY = ['document-requirement-types'] as const;
export const RULES_QUERY_KEY = ['document-requirement-rules'] as const;
export const reqQueryKey = (projectId: string) =>
  ['document-requirements', projectId] as const;

// ============================================================================
// Catalog: document_requirement_types
// ============================================================================

export function useDocumentRequirementTypes() {
  return useQuery({
    queryKey: TYPES_QUERY_KEY,
    queryFn: async () => {
      const res = await api.documentRequirements.listTypes();
      return res.requirement_types as DocumentRequirementType[];
    },
  });
}

export function useCreateRequirementType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      code: string;
      display_name?: string;
      description?: string;
      template_document_id?: string | null;
      default_recipient_role?: string;
    }) => api.documentRequirements.createType(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TYPES_QUERY_KEY });
      toast.success('Requirement type created');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to create type'),
  });
}

export function useUpdateRequirementType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      display_name?: string;
      description?: string;
      template_document_id?: string | null;
      default_recipient_role?: string;
      active?: boolean;
    }) => api.documentRequirements.updateType(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TYPES_QUERY_KEY });
      toast.success('Type updated');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to update type'),
  });
}

export function useDeleteRequirementType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.documentRequirements.deleteType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TYPES_QUERY_KEY });
      toast.success('Type deactivated');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to deactivate type'),
  });
}

// ============================================================================
// Formula: document_requirement_rules
// ============================================================================

export function useDocumentRequirementRules() {
  return useQuery({
    queryKey: RULES_QUERY_KEY,
    queryFn: async () => {
      const res = await api.documentRequirements.listRules();
      return res.rules as DocumentRequirementRule[];
    },
  });
}

export function useCreateRequirementRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      requirement_type_id: string;
      project_type_id: string;
      trigger_workflow_stage: string;
      due_offset_days?: number;
      recipient_role: string;
      condition_json?: Record<string, string[]> | null;
      priority?: number;
    }) => api.documentRequirements.createRule(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RULES_QUERY_KEY });
      toast.success('Rule created');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to create rule'),
  });
}

export function useUpdateRequirementRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; [key: string]: any }) =>
      api.documentRequirements.updateRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RULES_QUERY_KEY });
      toast.success('Rule updated');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to update rule'),
  });
}

export function useDeleteRequirementRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.documentRequirements.deleteRule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RULES_QUERY_KEY });
      toast.success('Rule deactivated');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to deactivate rule'),
  });
}

export function useImportRequirementRules() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      csvText,
      createMissingTypes,
    }: {
      csvText: string;
      createMissingTypes: boolean;
    }) => api.documentRequirements.importRules(csvText, createMissingTypes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RULES_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: TYPES_QUERY_KEY });
    },
    onError: (error: any) => toast.error(error.message || 'Import failed'),
  });
}

// ============================================================================
// Instances: project_document_requirements
// ============================================================================

export function useProjectDocumentRequirements(projectId: string) {
  return useQuery({
    queryKey: reqQueryKey(projectId),
    queryFn: async () => {
      const res = await api.documentRequirements.listForProject(projectId);
      return res.requirements as ProjectDocumentRequirement[];
    },
    enabled: !!projectId,
  });
}

export function useAddManualRequirement(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      requirement_type_id: string;
      recipient_role: string;
      custom_recipient_email?: string;
      due_date?: string;
    }) => api.documentRequirements.addManual(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reqQueryKey(projectId) });
      toast.success('Requirement added');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to add requirement'),
  });
}

export function useGenerateRequirements(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.documentRequirements.generate(projectId),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: reqQueryKey(projectId) });
      const count = data?.created ?? 0;
      toast.success(`Generated ${count} new requirement${count !== 1 ? 's' : ''}`);
    },
    onError: (error: any) =>
      toast.error(error.message || 'Failed to generate requirements'),
  });
}

export function useUpdateRequirement(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; [key: string]: any }) =>
      api.documentRequirements.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reqQueryKey(projectId) });
    },
    onError: (error: any) => toast.error(error.message || 'Failed to update'),
  });
}

// Optimistic: pending → sent
export function useSendRequirement(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requirementId: string) =>
      api.documentRequirements.send(requirementId),
    onMutate: async (requirementId: string) => {
      await queryClient.cancelQueries({ queryKey: reqQueryKey(projectId) });
      const previous = queryClient.getQueryData(reqQueryKey(projectId));
      queryClient.setQueryData(reqQueryKey(projectId), (old: any) => {
        if (!Array.isArray(old)) return old;
        return old.map((r: ProjectDocumentRequirement) =>
          r.id === requirementId
            ? { ...r, status: 'sent' as RequirementStatus, sent_at: new Date().toISOString() }
            : r,
        );
      });
      return { previous };
    },
    onError: (_err: any, _vars: any, context: any) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(reqQueryKey(projectId), context.previous);
      }
      toast.error('Failed to send');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reqQueryKey(projectId) });
      toast.success('Sent');
    },
  });
}

// Optimistic: received → approved
export function useApproveRequirement(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requirementId: string) =>
      api.documentRequirements.update(requirementId, { status: 'approved' }),
    onMutate: async (requirementId: string) => {
      await queryClient.cancelQueries({ queryKey: reqQueryKey(projectId) });
      const previous = queryClient.getQueryData(reqQueryKey(projectId));
      queryClient.setQueryData(reqQueryKey(projectId), (old: any) => {
        if (!Array.isArray(old)) return old;
        return old.map((r: ProjectDocumentRequirement) =>
          r.id === requirementId
            ? { ...r, status: 'approved' as RequirementStatus }
            : r,
        );
      });
      return { previous };
    },
    onError: (_err: any, _vars: any, context: any) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(reqQueryKey(projectId), context.previous);
      }
      toast.error('Failed to approve');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reqQueryKey(projectId) });
      toast.success('Approved');
    },
  });
}

// sent → received (upload + fulfill — simple invalidation, two-step async)
export function useFulfillRequirement(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requirementId,
      documentId,
    }: {
      requirementId: string;
      documentId: string;
    }) => api.documentRequirements.fulfill(requirementId, { document_id: documentId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reqQueryKey(projectId) });
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      toast.success('Marked received');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to mark received'),
  });
}

export function useWaiveRequirement(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requirementId,
      reason,
    }: {
      requirementId: string;
      reason: string;
    }) =>
      api.documentRequirements.update(requirementId, {
        status: 'waived',
        waived_reason: reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reqQueryKey(projectId) });
      toast.success('Waived');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to waive'),
  });
}

export function useDeleteRequirement(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requirementId: string) =>
      api.documentRequirements.deleteRequirement(requirementId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reqQueryKey(projectId) });
      toast.success('Deleted');
    },
    onError: (error: any) => toast.error(error.message || 'Failed to delete'),
  });
}
